'use client';

import React, { type InputHTMLAttributes } from 'react';
import { type VariantProps, cva, cx } from 'class-variance-authority';
import Icon, { type IconName } from '../Icon';

const variants = cva('', {
    variants: {
        grouped: {
            none: 'rounded-4xl',
            left: 'rounded-l-4xl',
            center: '',
            right: 'rounded-r-4xl',
        },
    },
    defaultVariants: {
        grouped: 'none',
    },
});

type HTMLInputProps = InputHTMLAttributes<HTMLInputElement>;

type Props<T extends keyof InputTypes.ValueMap> = Omit<
    HTMLInputProps,
    'type' | 'value' | 'onChange'
> & {
    type: T;
    value?:
        | InputTypes.ValueMap[NoInfer<T>]
        | null
        | ReturnType<typeof InputTypes.defaultValue<NoInfer<T>>>;
    onChange?: (
        value:
            | InputTypes.ValueMap[NoInfer<T>]
            | ReturnType<typeof InputTypes.defaultValue<NoInfer<T>>>,
        event: React.ChangeEvent<HTMLInputElement>,
    ) => void;
    iconBefore?: IconName;
    iconAfter?: IconName;
    before?: React.ReactNode;
    after?: React.ReactNode;
    className?: string;
    hasError?: boolean;
    hasClear?: boolean;
    grouped?: NonNullable<VariantProps<typeof variants>['grouped']>;
    disabled?: boolean;
    iconClassName?: string;
    focus?: boolean;
};

/** A better input component that natively supports other types than strings.
 *
 * @todo split this label into another component so it can be reused */
export default function InputV2<const T extends keyof InputTypes.ValueMap>({
    iconBefore,
    iconAfter,
    before,
    after,
    className,
    iconClassName,
    hasError = false,
    grouped = 'none',
    hasClear = false,
    disabled,
    focus = false,
    type = 'text' as any,
    ...props
}: Props<T>) {
    const hasIconBefore = !!iconBefore;
    const hasIconAfter = !!iconAfter;

    return (
        <label
            className={cx(
                'relative flex-1 focus-within:z-1',
                'flex w-full items-center gap-3 border bg-white leading-none text-primary transition',
                'px-5 py-4',
                disabled
                    ? 'cursor-not-allowed bg-blue-100 text-blue-400'
                    : hasError
                      ? 'text-orange-700 placeholder-orange-300 ring-orange-700'
                      : 'border-blue-200 focus-within:border-turqoise hover:border-blue-900 hover:focus-within:border-turqoise',
                className,
                variants({ grouped }),
            )}
        >
            {before && <LabelText>{before}</LabelText>}
            {hasIconBefore && (
                <InputIcon hasError={hasError} name={iconBefore} className={iconClassName} />
            )}

            <input
                {...props}
                type={type}
                className={cx(
                    // @TODO: This breaks NumberInputs, value is not visible: px-6 => fix!
                    'w-full appearance-none border-0 bg-transparent p-0 outline-hidden placeholder:text-blue-400 focus:ring-0',
                    disabled && 'user-select-none cursor-not-allowed',
                )}
                disabled={disabled}
                aria-invalid={hasError}
                value={
                    props.value === null || props.value === undefined
                        ? ''
                        : InputTypes.toString(type, props.value as any)
                }
                // This is a bit buggy atm but could be a nice to have
                // onKeyDown={
                //     type === 'number'
                //         ? (e) => {
                //               if (e.key.length === 1 && Number.isNaN(parseInt(e.key, 10))) {
                //                   e.preventDefault()
                //                   e.stopPropagation()
                //               }
                //           }
                //         : undefined
                // }
                onChange={e => {
                    const value =
                        e.currentTarget.value.length === 0
                            ? null
                            : (InputTypes.fromString(type ?? 'text', e.currentTarget.value) as any);

                    if (type === 'number' && Number.isNaN(value)) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                    }

                    props.onChange?.(value, e);
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={focus}
            />
            {hasIconAfter && (
                <InputIcon hasError={hasError} name={iconAfter} className={iconClassName} />
            )}

            {hasClear && props.value && (
                <button
                    type="button"
                    onClick={() =>
                        props.name &&
                        props.onChange?.(InputTypes.defaultValue(type), {
                            target: { value: '', name: props.name },
                        } as any)
                    }
                >
                    <Icon name="close" size="xs" className="mr-4 opacity-50 hover:opacity-100" />
                </button>
            )}
            {after && <LabelText>{after}</LabelText>}
        </label>
    );
}

namespace InputTypes {
    export type ValueMap = {
        number: number;
        date: Date;
    } & Record<'url' | 'tel' | 'text', string>;

    export function toString<T extends keyof InputTypes.ValueMap>(
        type: T,
        value: InputTypes.ValueMap[T],
    ) {
        switch (type) {
            case 'date':
                return (value as InputTypes.ValueMap['date']).toISOString().split('T').at(0);
            default:
                return value.toString();
        }
    }

    export function fromString<T extends keyof InputTypes.ValueMap>(
        type: T,
        value: string,
    ): InputTypes.ValueMap[T] {
        switch (type) {
            case 'date':
                return new Date(value) as any;
            case 'number':
                return Number(value) as any;
            default:
                return value as any;
        }
    }

    export function defaultValue<T extends keyof InputTypes.ValueMap>(
        type: T,
    ): T extends 'date' ? null : T extends 'number' ? number : string {
        switch (type) {
            case 'date':
                return null as any;
            case 'number':
                return 0 as any;
            default:
                return '' as any;
        }
    }
}

function InputIcon(props: { hasError: boolean; name: IconName; className?: string }) {
    return (
        <Icon
            className={cx(
                props.hasError ? 'text-orange-700' : 'text-turqoise-500',
                props.className,
            )}
            name={props.name}
        />
    );
}

function LabelText(props: { children?: React.ReactNode }) {
    return <span className="text-sm font-normal text-blue-400">{props.children}</span>;
}
